import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Sparkles, Loader2, CheckCircle2, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CANDIDATES = [
  {
    id: 1,
    label: "Modernne Nordic kliiniku interjöör",
    desc: "Avar valgusküllane ruum, valge orhidee, sapphire + teal aktsendid",
    filename: "d05c5b83d88149f080a423b7a43479fd.png",
  },
  {
    id: 2,
    label: "Endokrinoloog kilpnäärme uuringul",
    desc: "Inimkeskne — arst ja patsient hetkel UH uuringul, soe atmosfäär",
    filename: "56822ebb5f654b509d51a17791682cfc.png",
  },
  {
    id: 3,
    label: "Kliiniku arhitektuurne välisvaade",
    desc: "Nordic minimal — klaas + puit fassaad, hommiku-Baltic valgus",
    filename: "d8d1c651dd2e45fb9cd6ef491e4655f1.png",
  },
  {
    id: 4,
    label: "Käed — arst ja patsient",
    desc: "Empaatiline lähivaade kätest, hoolitsus ja usaldus",
    filename: "a10b465782544786a99b706379d4cfba.png",
  },
];

export default function PictureChooser() {
  const [generating, setGenerating] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [extras, setExtras] = useState([]);

  const generateCustom = async () => {
    if (!customPrompt.trim() || generating) return;
    setGenerating(true);
    try {
      const r = await fetch(`${API}/api/ai/generate-image`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: customPrompt }),
      });
      const data = await r.json();
      if (data.url) {
        setExtras((e) => [...e, { id: `c${e.length + 1}`, label: "Sinu prompt", desc: customPrompt, url: `${API}${data.url}` }]);
        setCustomPrompt("");
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="container-page py-12">
      <Helmet>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="chip mb-3 inline-flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> AI GENEREERITUD</div>
      <h1 className="section-title mb-3">Esilehe pildi kandidaadid</h1>
      <p className="text-surface-inkSoft text-[15px] mb-10 max-w-2xl">
        4 Gemini Nano Bananaga genereeritud kandidaati. Saada mulle vestluses number 1–4 ja paneme valitud pildi avalehele.
        Kui ükski ei sobi, saad allpool genereerida lisapildi oma kirjelduse järgi.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {CANDIDATES.map((c) => (
          <div key={c.id} className="card-clinic !p-0 overflow-hidden" data-testid={`pic-${c.id}`}>
            <div className="aspect-[4/3] bg-sapphire-50 overflow-hidden">
              <img src={`${API}/api/static/ai-images/${c.filename}`} alt={c.label} className="w-full h-full object-cover" />
            </div>
            <div className="p-5">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-sapphire-700 text-white flex items-center justify-center font-bold text-lg shrink-0">{c.id}</div>
                <div>
                  <div className="text-[12px] uppercase tracking-wider font-semibold text-teal-500">Pilt {c.id}</div>
                  <p className="text-[14px] font-semibold text-surface-ink mt-1">{c.label}</p>
                  <p className="text-[13px] text-surface-inkSoft mt-1 leading-relaxed">{c.desc}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
        {extras.map((c) => (
          <div key={c.id} className="card-clinic !p-0 overflow-hidden border-teal-300/60">
            <div className="aspect-[4/3] bg-sapphire-50 overflow-hidden">
              <img src={c.url} alt={c.label} className="w-full h-full object-cover" />
            </div>
            <div className="p-5">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-teal-500 text-white flex items-center justify-center font-bold shrink-0">{c.id}</div>
                <div>
                  <div className="text-[12px] uppercase tracking-wider font-semibold text-teal-500">Sinu pilt</div>
                  <p className="text-[13px] text-surface-inkSoft mt-1 leading-relaxed">"{c.desc}"</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 card-clinic">
        <h2 className="text-xl font-semibold text-surface-ink mb-2">Genereeri lisapilt</h2>
        <p className="text-[14px] text-surface-inkSoft mb-4">Kirjuta inglise keeles, milline pilt sa soovid (modell töötab paremini inglise keeles).</p>
        <textarea
          value={customPrompt}
          onChange={(e) => setCustomPrompt(e.target.value)}
          rows={3}
          placeholder='Example: "A serene wellness photo of a healthy middle-aged Estonian woman walking on a sunlit Tallinn old town street, soft pastel tones, photo-realistic, 4:3"'
          className="w-full px-4 py-3 rounded-lg bg-white border border-surface-outline/60 text-[14px] focus:outline-none focus:border-sapphire-500 focus:ring-2 focus:ring-teal-200/40 mb-4"
        />
        <button onClick={generateCustom} disabled={generating || !customPrompt.trim()} className="btn-primary">
          {generating ? <><Loader2 className="h-4 w-4 animate-spin" /> Genereerin…</> : <><Sparkles className="h-4 w-4" /> Genereeri pilt</>}
        </button>
      </div>

      <div className="mt-8 rounded-2xl bg-sapphire-50/60 border border-sapphire-300/40 p-6">
        <p className="text-[14px] text-surface-ink">
          <span className="font-semibold">Vali nüüd:</span> Vasta vestluses
          <span className="font-mono mx-1 px-2 py-0.5 bg-white rounded">1</span>,
          <span className="font-mono mx-1 px-2 py-0.5 bg-white rounded">2</span>,
          <span className="font-mono mx-1 px-2 py-0.5 bg-white rounded">3</span> või
          <span className="font-mono mx-1 px-2 py-0.5 bg-white rounded">4</span> — paneme selle kohe avalehele.
        </p>
      </div>
    </div>
  );
}
